import type { ReactNode } from "react";

import { PillTabNav } from "./PillTabNav";
import {
  profileShellBackLabelKey,
  profileShellTabs,
  type ProfileShellSurfaceType,
  type ProfileShellTabDefinition,
  type ProfileShellTabId,
} from "./profileShell";

export type ProfileShellTabsProps = Readonly<{
  activeTabId: ProfileShellTabId;
  ariaLabel: string;
  componentInstance: string;
  labelForTab: (tab: ProfileShellTabDefinition) => string;
  onTabChange: (tabId: ProfileShellTabId) => void;
  tabComponentInstancePrefix: string;
  tabs?: readonly ProfileShellTabDefinition[];
  /** When true, the tab bar sticks below the viewport top while scrolling. */
  sticky?: boolean;
  className?: string;
}>;

/**
 * Profile-shell typed wrapper around the shared {@link PillTabNav} menu.
 */
export function ProfileShellTabs({
  activeTabId,
  ariaLabel,
  componentInstance,
  labelForTab,
  onTabChange,
  tabComponentInstancePrefix,
  tabs = profileShellTabs,
  sticky = false,
  className,
}: ProfileShellTabsProps) {
  return (
    <PillTabNav
      activeId={activeTabId}
      ariaLabel={ariaLabel}
      className={className}
      componentInstance={componentInstance}
      sticky={sticky}
      items={tabs.map((tab) => ({
        id: tab.id,
        label: labelForTab(tab),
        componentInstance: `${tabComponentInstancePrefix}${tab.componentInstance}`,
      }))}
      onChange={(id) => onTabChange(id as ProfileShellTabId)}
    />
  );
}

export type ProfileShellBackButtonProps = Readonly<{
  arrivedFromDirectory?: boolean;
  className: string;
  componentInstance: string;
  href?: string;
  icon?: ReactNode;
  label?: string;
  labelForKey?: (key: string) => string;
  onClick?: () => void;
  surface: ProfileShellSurfaceType;
}>;

export function ProfileShellBackButton({
  arrivedFromDirectory,
  className,
  componentInstance,
  href,
  icon,
  label,
  labelForKey,
  onClick,
  surface,
}: ProfileShellBackButtonProps) {
  const labelKey = profileShellBackLabelKey({ surface, arrivedFromDirectory });
  const resolvedLabel = label ?? labelForKey?.(labelKey) ?? labelKey;
  const content = (
    <>
      {icon}
      {resolvedLabel}
    </>
  );

  if (href) {
    return (
      <a
        className={className}
        data-component-instance={componentInstance}
        href={href}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      data-component-instance={componentInstance}
      onClick={onClick}
    >
      {content}
    </button>
  );
}
